import { Link } from 'react-router-dom';
import { Star, MapPin, Clock, Phone, Trophy, ShieldAlert, Zap, PhoneCall } from 'lucide-react';
import VerifiedBadge from './VerifiedBadge';

export default function TradeCard({ trade, compact = false }) {
  const cats = (trade.categories || []).slice(0, 2);

  return (
    <Link
      to={`/tradie/${trade.id}`}
      data-testid={`trade-card-${trade.id}`}
      className="group block border border-[#E5E4E0] bg-white p-6 transition-all hover:border-[#1A362D] hover:shadow-sm"
    >
      <div className="flex gap-5">
        {trade.avatar_url ? (
          <img src={trade.avatar_url} alt={trade.business_name} className="h-20 w-20 shrink-0 object-cover" />
        ) : (
          <div className="flex h-20 w-20 shrink-0 items-center justify-center bg-[#F3F2EE] text-2xl font-heading font-bold text-[#1A362D]">
            {trade.business_name?.charAt(0)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-heading text-xl font-bold tracking-tight text-[#1C1B1A] transition-colors group-hover:text-[#1A362D]" data-testid="trade-name">
              {trade.business_name}
            </h3>
            {trade.verified && <VerifiedBadge variant="inline" />}
            {trade.live_now && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#2A9D8F]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#2A9D8F]" data-testid="live-now-badge" title="Currently on the tools — heartbeat within the last 8 hours">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#2A9D8F] opacity-75"></span>
                  <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#2A9D8F]"></span>
                </span>
                Live now
              </span>
            )}
            {trade.tradie_of_the_month && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[#D96C4E] bg-[#D96C4E] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white" data-testid="tom-badge" title="Perth Tradie of the Month">
                ★ Tradie of the Month
              </span>
            )}
            {trade.claimed === false && (
              <span className="inline-flex items-center gap-1 rounded-full border border-[#B08911] bg-[#B08911]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#B08911]" data-testid="unclaimed-badge" title="This listing was created from public info. Owner has not claimed it yet.">
                Unclaimed
              </span>
            )}
            {trade.top_rated && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#1A362D] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white" data-testid="top-rated-badge">
                <Trophy size={12} /> Top rated
              </span>
            )}
            {trade.emergency_24_7 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#E76F51] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white" data-testid="emergency-badge">
                <Zap size={12} /> 24/7
              </span>
            )}
            {trade.available_today && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#D96C4E]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#D96C4E]">
                Available today
              </span>
            )}
            {trade.compliance_state === 'expired' && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#E76F51]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#E76F51]" data-testid="compliance-expired">
                <ShieldAlert size={12} /> Docs expired
              </span>
            )}
          </div>

          <div className="mt-1 flex flex-wrap gap-2 text-xs uppercase tracking-[0.15em] text-[#8A8988]">
            {cats.map((c) => <span key={c}>{c.replace('_', ' ')}</span>)}
          </div>

          {!compact && trade.bio && (
            <p className="mt-3 line-clamp-2 text-sm leading-relaxed text-[#5C5B5A]">{trade.bio}</p>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#5C5B5A]">
            <span className="inline-flex items-center gap-1.5">
              <Star size={14} className="fill-[#E9C46A] text-[#E9C46A]" />
              <span className="font-semibold text-[#1C1B1A]" data-testid="trade-rating">{trade.avg_rating || '—'}</span>
              <span>({trade.review_count})</span>
            </span>
            <span className="inline-flex items-center gap-1.5 font-semibold text-[#1A362D]" data-testid="reputation-score" title="Reputation score">
              <Trophy size={12} /> {trade.reputation_score}
            </span>
            {trade.answer_rate != null && (
              <span className="inline-flex items-center gap-1.5 font-semibold text-[#2A9D8F]" data-testid="answer-rate" title="How often they answer their phone">
                <PhoneCall size={12} /> {trade.answer_rate}% answer
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <MapPin size={14} /> {trade.suburb}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={14} /> {trade.response_time}
            </span>
            {trade.hourly_rate && (
              <span className="inline-flex items-center gap-1.5 text-[#1A362D] font-semibold">
                ${trade.hourly_rate}/hr
              </span>
            )}
          </div>
        </div>

        {trade.phone && (
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.location.href = `tel:${trade.phone}`; }}
            data-testid={`call-btn-${trade.id}`}
            className="hidden shrink-0 items-center gap-2 self-start bg-[#D96C4E] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#C2593D] md:inline-flex"
          >
            <Phone size={16} /> Call
          </button>
        )}
      </div>
    </Link>
  );
}
